<template>
  <form-section-checkbox
    v-if="model.throttling_data"
    :title="$t('CampaignForm_Schedule_Campaign_Throttling_Title')"
    :description="$t('CampaignForm_Schedule_Campaign_Throttling_Description')"
    class="Throttling__Checkbox"
    v-model="model.throttling"
    :disabled="readonly"
  >
    <el-row :gutter="20">
      <el-col :span="20" :offset="1">
        <el-row class="m-top-10">
          <form-section>
            <div class="ChannelThrottlingForm__Container">
              <el-row class="label">
                <div class="title Text__Color2">
                  {{
                    $t(
                      'CampaignForm_Schedule_Campaign_Throttling_SendLimitTitle'
                    )
                  }}
                </div>
                <div
                  class="description Text__Color2"
                  v-html="
                    $t(
                      'CampaignForm_Schedule_Campaign_Throttling_SendLimitDesc'
                    )
                  "
                ></div>
              </el-row>
              <el-row class="m-top-15">
                <div class="ChannelThrottlingForm__Limits">
                  <el-input-number
                    v-model="recipientsPerHour"
                    class="m-right-10 ChannelThrottlingForm__Limits__Input Text__Color1"
                    :controls="false"
                    :min="recipientsPerHour ? throttlingMinLimit : null"
                    :max="10000000000000000"
                    :disabled="readonly"
                    :placeholder="
                      $t(
                        'CampaignForm_Schedule_Campaign_Throttling_ChannelLimits_Unlimited'
                      )
                    "
                    type="number"
                  >
                  </el-input-number>
                  <div class="description Text__Color2">
                    {{
                      $t(
                        'CampaignForm_Schedule_Campaign_Throttling_RecipientsPerHour'
                      )
                    }}
                  </div>
                </div>
              </el-row>
            </div>
          </form-section>
        </el-row>
        <el-row class="label m-top-10">
          <div class="title Text__Color2">
            {{ $t('CampaignForm_Schedule_Campaign_Throttling_DeliveryTitle') }}
          </div>
          <div class="description Text__Color2">
            {{ $t('CampaignForm_Schedule_Campaign_Throttling_DeliveryDesc') }}
          </div>
        </el-row>
        <el-row class="m-top-10 flex-row ChannelDeliveryForm__Container">
          <el-dropdown
            trigger="click"
            placement="bottom-start"
            class="m-right-30 Text__Color1"
          >
            <el-input
              :value="deliveryWindowText"
              :disabled="readonly"
            ></el-input>
            <el-dropdown-menu
              slot="dropdown"
              class="Text__Color1"
              v-if="!readonly"
            >
              <checkbox-group
                v-model="model.throttling_data.delivery_window.send_days"
              >
                <el-checkbox
                  class="checkbox"
                  v-for="(label, val) in dayOfWeekLabelsFull"
                  :key="val"
                  :label="parseInt(val)"
                  :disabled="isSendDaysCheckboxDisabled(parseInt(val))"
                >
                  {{ $t(label) }}
                </el-checkbox>
              </checkbox-group>
            </el-dropdown-menu>
          </el-dropdown>
          <el-time-picker
            :disabled="readonly"
            v-model="model.throttling_data.delivery_window.start_hour"
            format="HH:mm"
            value-format="HH:mm"
            :picker-options="{
              start: '00:00',
              end: '23:00'
            }"
            class="m-right-10 Text__Color1"
          >
          </el-time-picker>
          <span class="Time__Divider">-</span>
          <el-time-picker
            :disabled="readonly"
            format="HH:mm"
            value-format="HH:mm"
            v-model="model.throttling_data.delivery_window.end_hour"
            :picker-options="{
              start: '00:00',
              end: '23:00'
            }"
            class="Text__Color1"
          >
          </el-time-picker>
          <el-select
            v-model="model.throttling_data.delivery_window.timezone"
            filterable
            class="m-left-5 Text__Color1"
            :disabled="readonly"
          >
            <el-option
              v-for="(label, value) in timezones"
              v-if="value !== ''"
              :key="value"
              :value="value"
              :label="label"
            >
              <span v-tooltip>{{ label }}</span>
            </el-option>
          </el-select>
        </el-row>
      </el-col>
    </el-row>
  </form-section-checkbox>
</template>

<script>
import FormSectionCheckbox from '@/common/components/form/FormSectionCheckbox'
import FormSection from '@/common/components/form/FormSection'
import {
  dayOfWeekLabels,
  dayOfWeekLabelsFull
} from '@/modules/campaign-manage/enum/Labels'
import { CheckboxGroup } from 'element-ui'
import { mapGetters } from 'vuex'

export default {
  name: 'CampaignThrottlingComponent',
  components: {
    FormSectionCheckbox,
    FormSection,
    CheckboxGroup
  },
  props: {
    model: Object,
    isThrottlingAvailable: Boolean,
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dayOfWeekLabelsFull,
      dayOfWeekLabels,
      recipientsPerHour: 0,
      throttlingMinLimit: 1,
      initiated: false
    }
  },
  mounted() {
    this.initThrottlingData()
  },
  methods: {
    initThrottlingData() {
      if (!this.isThrottlingAvailable) {
        this.model.throttling = 0
        this.model.throttling_data = null
      } else {
        if (this.model.throttling_data === null) {
          this.model.throttling_data = this.initialData
        }
        this.recipientsPerHour = this.model.throttling_data.recipients_per_hour
        this.initiated = true
      }
    },
    isSendDaysCheckboxDisabled(value) {
      let sendDays = this.model.throttling_data.delivery_window.send_days
      return sendDays.length === 1 && sendDays.includes(value)
    }
  },
  computed: {
    deliveryWindowText() {
      let text = ''
      let days = this.model.throttling_data.delivery_window.send_days
      if (days.length === 7) {
        text = this.$t(dayOfWeekLabels[1]) + ' - ' + this.$t(dayOfWeekLabels[7])
      } else {
        days.forEach(item => {
          text += this.$t(dayOfWeekLabels[item]) + ', '
        })
      }

      return text.replace(/,\s*$/, '')
    },
    initialData() {
      return {
        delivery_window: {
          send_days: [1, 2, 3, 4, 5, 6, 7],
          start_hour: null,
          end_hour: null,
          timezone: this.model.project.timezone
        },
        recipients_per_hour: 0
      }
    },
    ...mapGetters(['timezones'])
  },
  watch: {
    isThrottlingAvailable(newVal) {
      this.initThrottlingData()
    },
    recipientsPerHour(newVal) {
      if (this.initiated) {
        if (!newVal) {
          this.$delete(this.model.throttling_data, 'recipients_per_hour')
        } else {
          this.$set(this.model.throttling_data, 'recipients_per_hour', newVal)
        }
      }
    }
  }
}
</script>

<style scoped>
>>> .title {
  font-weight: 600;
  font-size: 14px;
}

>>> .el-menu-item:not(.is-active) .title {
  font-weight: 400;
}

.Throttling__Checkbox >>> .el-form-item__content .title {
  color: #4b515f !important;
}

.Throttling__Checkbox >>> .el-form-item__content .description {
  color: #5c6374 !important;
}

.Text__Color1,
.Text__Color1 >>> .el-input__inner,
.Text__Color1 >>> .el-input__inner,
.Text__Color1 >>> .el-checkbox__label {
  color: #4b515f !important;
}

.Text__Color2 {
  color: #5c6374 !important;
}
>>> .el-checkbox__input.is-checked .el-checkbox__inner {
  border-color: #1d87c8 !important;
  background-color: #1d87c8;
}

>>> .el-checkbox__inner {
  border-color: #dcdfe6 !important;
  background-color: #fff;
  border-radius: 4px;
}

>>> .el-checkbox-group {
  display: flex;
  flex-direction: column;
  width: 250px;
}

>>> .checkbox {
  padding: 0 10px;
  height: 30px;
  display: flex;
  align-items: center;
}

>>> .checkbox .el-checkbox__label {
  line-height: 0;
}

>>> .el-date-editor--time-select {
  width: 100px;
}

>>> .el-checkbox__input.is-checked .el-checkbox__inner {
  border-color: #1d87c8 !important;
  background-color: #1d87c8;
}

>>> .el-checkbox.is-bordered.is-checked {
  border-color: #1d87c8;
  background-color: #e6f3fb;
}

>>> .el-checkbox__inner {
  border-color: #dcdfe6 !important;
  background-color: #fff;
  width: 20px;
  height: 20px;
  border-radius: 4px;
}

>>> .el-checkbox__inner::after {
  height: 8px;
  border: 2px solid #fff;
  border-left: 0;
  border-top: 0;
  top: 3px;
  left: 6px;
  width: 4px;
}

>>> .el-menu {
  border-right: 0;
  background: #f8fafc;
  border-radius: 4px;
}

>>> .el-menu-item.is-active {
  background: #fff;
}
>>> .el-menu-item div {
  align-items: center;
}

>>> .el-menu-item div .icon-ct {
  font-size: 20px;
}

>>> .el-menu-item div .icon-ct .icon {
  margin-bottom: 3px;
}

.Time__Divider {
  display: flex;
  align-items: center;
  margin-right: 10px;
}

.ChannelThrottlingForm__Container {
  display: flex;
  flex-direction: column;
  padding: 30px;
}

.ChannelDeliveryForm__Container {
  align-items: center;
  font-size: 14px;
}

.ChannelThrottlingForm__Limits {
  display: flex;
  align-items: center;
}

>>> .ChannelThrottlingForm__Limits .el-input {
  width: 100px;
}

.ChannelThrottlingForm__Limits__Input {
  width: 100px;
}

>>> .ChannelThrottlingForm__Limits__Input .el-input__inner {
  text-align: left;
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/schedule/CampaignThrottlingComponent.vue